import React, { useState } from 'react';
import { Modal, Button, message } from 'antd';
import {
  useTeamSetup,
  TeamFields,
  CurrentWorkFields,
  StandupFields,
} from './teamSetup';
import './teams.css';

// In-app "Create a team" modal. A 3-step mini-wizard that reuses the SAME
// per-team steps as the onboarding wizard (Team / Current work / Standup) via
// the shared teamSetup module — so a team made here is configured identically
// to one made during onboarding (current_work + standup_config included). The
// workspace-level connect steps and the onboarding Preview are intentionally
// omitted; Slack/Jira/GitHub are already connected for an existing workspace.

const MSTEPS = [
  { key: 'Team', title: 'Create a team', label: 'New team' },
  { key: 'Current work', title: "Where does this team's work live?", label: 'Current work' },
  { key: 'Standup', title: 'Set up the standup', label: 'Standup' },
];

const CreateTeamFlow = ({ wId, visible, onClose, onCreated }) => {
  const t = useTeamSetup(wId);
  const [mStep, setMStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const stepKey = MSTEPS[mStep].key;
  const isLast = mStep === MSTEPS.length - 1;

  const nextDisabled =
    (stepKey === 'Team' && !t.teamValid) ||
    (stepKey === 'Current work' && !t.currentWorkValid);

  const handleNext = async () => {
    try {
      setSaving(true);
      if (stepKey === 'Team') {
        if (!t.teamValid) {
          setSaving(false);
          return message.error(t.name.trim() ? 'Pick a Slack channel' : 'Enter a team name');
        }
        await t.saveTeamStep();
      } else if (stepKey === 'Current work') {
        if (!t.currentWorkValid) {
          setSaving(false);
          return message.error('Pick a Jira project and which issues count as current work');
        }
        await t.saveCurrentWork();
      } else if (stepKey === 'Standup') {
        await t.saveStandup();
        setSaving(false);
        message.success('Team created');
        if (onCreated) onCreated({ _id: t.teamId, name: t.name });
        return;
      }
      setSaving(false);
      setMStep(mStep + 1);
    } catch (e) {
      setSaving(false);
      message.error('Could not save. Try again.');
    }
  };

  // Footer is pinned by antd below the scrollable body, so the actions are
  // always reachable regardless of step height.
  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ color: '#8a93a6', fontSize: 13 }}>Step {mStep + 1} of {MSTEPS.length}</span>
      <div style={{ display: 'flex', gap: 10 }}>
        {mStep > 0 ? (
          <Button onClick={() => setMStep(mStep - 1)} disabled={saving}>Back</Button>
        ) : (
          <Button onClick={onClose}>Cancel</Button>
        )}
        <Button
          className="tpr-sq__primary"
          type="primary"
          loading={saving}
          disabled={nextDisabled}
          onClick={handleNext}
        >
          {isLast ? 'Create team' : 'Next'}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={footer}
      width={620}
      centered
      destroyOnClose
      className="tpr-sq"
      bodyStyle={{ maxHeight: '68vh', overflowY: 'auto', overflowX: 'hidden' }}
    >
      <div className="tpr-sq__label">{MSTEPS[mStep].label}</div>
      <div className="tpr-sq__title">{MSTEPS[mStep].title}</div>
      {mStep === 0 && (
        <div className="tpr-sq__subtitle">
          Slack and Jira are already connected for your workspace. Point Troopr at where your team
          works, and it pulls in the rest.
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {stepKey === 'Team' && <TeamFields t={t} />}
        {stepKey === 'Current work' && <CurrentWorkFields t={t} />}
        {stepKey === 'Standup' && <StandupFields t={t} />}
      </div>
    </Modal>
  );
};

// Remount on each open (key flips) so the shared hook starts from a clean slate.
const CreateTeamModal = ({ wId, visible, onClose, onCreated }) => (
  <CreateTeamFlow
    key={visible ? 'open' : 'closed'}
    wId={wId}
    visible={visible}
    onClose={onClose}
    onCreated={onCreated}
  />
);

export default CreateTeamModal;
